import { useMemo, useRef, useState } from 'react';
import { AddGuestToFunctions } from '../../api/api';
import { isPastFunction, nameInput, tenDigits, toggleWithWedding, newGuestFunctionIds } from '../../utils/helpers';

// "Quick add" — fast data entry of many guests in one grid: one line per guest
// (surname, name, mobile) with a checkbox per open function. A new line copies
// the functions of the line above, so a list for the same functions is only
// names and numbers. The Wedding rule applies per line (toggleWithWedding).
// Saved lines leave the grid; lines with a problem stay, marked, with the reason.
// Used by useMembers, which hands in what it already knows.
const START_ROWS = 5;
const lc = (v) => String(v || '').trim().toLowerCase();

export function useQuickAdd({
  event, organiser, user, functions, rows, isMarriage, lockedSide, defaultSide, sideMissing,
  fnFilter, refresh, toast, readDefaults, writeDefaults, rowFunctionIds,
}) {
  const me = user.UserId;
  const keySeq = useRef(0);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickRows, setQuickRows] = useState([]);
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickProgress, setQuickProgress] = useState({ done: 0, total: 0 });

  // Only functions that are not over can be invited to.
  const quickFunctions = useMemo(() => functions.filter(f => !isPastFunction(f)), [functions]);

  const newRow = (FunctionIds = [], weddingOff = false) => {
    keySeq.current += 1;
    return { key: keySeq.current, Surname: '', GuestName: '', MobileNumber: '', FunctionIds, weddingOff, error: '' };
  };
  const isFilled = (r) => !!(String(r.GuestName).trim() || String(r.Surname).trim() || String(r.MobileNumber).trim());

  // Where a fresh grid starts: the filtered function (plus the Wedding), else
  // the functions used last time, else nothing.
  const startingFunctions = () => {
    const open = new Set(quickFunctions.map(f => String(f.FunctionId)));
    const preset = quickFunctions.find(f => String(f.FunctionId) === String(fnFilter));
    if (preset) return newGuestFunctionIds([String(preset.FunctionId)], quickFunctions);
    return newGuestFunctionIds((readDefaults(event.EventId).FunctionIds || []).map(String).filter(id => open.has(id)), quickFunctions);
  };

  const openQuick = () => {
    if (sideMissing) { toast('Ask your event admin to set your side (Groom or Bride) before adding guests'); return; }
    if (!quickFunctions.length) { toast('No open function to invite guests to'); return; }
    const fns = startingFunctions();
    setQuickRows(Array.from({ length: START_ROWS }, () => newRow(fns)));
    setQuickProgress({ done: 0, total: 0 });
    setQuickOpen(true);
  };
  const closeQuick = () => { if (!quickSaving) setQuickOpen(false); };

  const setQuickField = (key, field, value) => setQuickRows(list => list.map(r => {
    if (r.key !== key) return r;
    const v = field === 'MobileNumber' ? tenDigits(value) : nameInput(value);
    return { ...r, [field]: v, error: '' };
  }));

  const toggleQuickFunction = (key, functionId) => setQuickRows(list => list.map(r => {
    if (r.key !== key) return r;
    const res = toggleWithWedding(r.FunctionIds, functionId, quickFunctions, r.weddingOff);
    return { ...r, FunctionIds: res.ids, weddingOff: res.weddingOff, error: '' };
  }));

  // Column header checkbox: tick this function for every line, or clear it
  // from every line when all of them have it already.
  const columnState = (functionId) => {
    const id = String(functionId);
    const n = quickRows.filter(r => r.FunctionIds.includes(id)).length;
    return n === 0 ? 'none' : n === quickRows.length ? 'all' : 'some';
  };
  const toggleQuickColumn = (functionId) => {
    const id = String(functionId);
    const allOn = columnState(id) === 'all';
    setQuickRows(list => list.map(r => {
      const has = r.FunctionIds.includes(id);
      if (allOn === has) {
        const res = toggleWithWedding(r.FunctionIds, id, quickFunctions, r.weddingOff);
        return { ...r, FunctionIds: res.ids, weddingOff: res.weddingOff };
      }
      return r;
    }));
  };

  // New lines copy the functions of the last line.
  const addQuickRows = (n = 1) => setQuickRows(list => {
    const last = list[list.length - 1];
    const fns = last ? last.FunctionIds : startingFunctions();
    return [...list, ...Array.from({ length: n }, () => newRow(fns, last?.weddingOff || false))];
  });
  const removeQuickRow = (key) => setQuickRows(list => (list.length > 1 ? list.filter(r => r.key !== key) : list));

  const filledCount = quickRows.filter(isFilled).length;

  // Why this line cannot be saved ('' = fine). `earlier` = lines above it.
  const problemFor = (r, earlier) => {
    const name = String(r.GuestName).trim();
    const mobile = String(r.MobileNumber).trim();
    if (!name) return 'Name is required';
    if (mobile && !/^\d{10}$/.test(mobile)) return 'Mobile must be 10 digits';
    if (!r.FunctionIds.length) return 'Tick at least one function';
    const person = `${lc(r.Surname)}|${lc(name)}`;
    const twin = earlier.find(o => (mobile && o.MobileNumber === mobile) || `${lc(o.Surname)}|${lc(o.GuestName)}` === person);
    if (twin) return 'Listed twice in this grid';
    // Already invited to one of these functions (same number, or same name)?
    const clash = rows.find(g => rowFunctionIds(g).some(id => r.FunctionIds.includes(String(id)))
      && ((mobile && String(g.MobileNumber || '') === mobile) || `${lc(g.Surname)}|${lc(g.GuestName)}` === person));
    if (clash) {
      const by = String(clash.CreatedBy) === String(me) ? 'you' : ((clash.AddedByName || '').trim() || 'another member');
      return `Already invited to ${clash.FunctionName || 'one of these functions'} (added by ${by})`;
    }
    return '';
  };

  const saveQuick = async () => {
    const filled = quickRows.filter(isFilled);
    if (!filled.length) { toast('Type at least one guest'); return; }
    // Check every line first; bad lines stay in the grid with their reason.
    const checked = filled.map((r, i) => ({ r, error: problemFor(r, filled.slice(0, i)) }));
    const good = checked.filter(c => !c.error).map(c => c.r);
    const errors = new Map(checked.filter(c => c.error).map(c => [c.r.key, c.error]));

    setQuickSaving(true);
    setQuickProgress({ done: 0, total: good.length });
    const saved = new Set();
    const addedByName = `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || null;
    const side = isMarriage ? (lockedSide || defaultSide) : 'Common';
    for (const r of good) {
      try {
        await AddGuestToFunctions({
          EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionIds: r.FunctionIds,
          Surname: String(r.Surname).trim() || null, GuestName: String(r.GuestName).trim(),
          MobileNumber: r.MobileNumber || null, Side: side, Visibility: 'Private', ExpectedCount: 1,
          InviteStatus: 'Pending', IsFamilyHead: 0, FamilyMembers: [],
          AddedByName: addedByName, AddedByMobile: user.MobileNumber || null,
          Valid: 1, CreatedBy: me, UpdatedBy: me,
        });
        saved.add(r.key);
      } catch {
        errors.set(r.key, 'Save failed - check your connection and save again');
      }
      setQuickProgress(p => ({ ...p, done: p.done + 1 }));
    }
    setQuickSaving(false);

    const lastFns = good.length ? good[good.length - 1].FunctionIds : null;
    if (lastFns) writeDefaults(event.EventId, { ...readDefaults(event.EventId), FunctionIds: lastFns });
    // Keep only what still needs attention (errors + untouched blank lines);
    // top the grid back up so the next batch can be typed straight away.
    setQuickRows(list => {
      const left = list.filter(r => !saved.has(r.key)).map(r => ({ ...r, error: errors.get(r.key) || '' }));
      const fns = lastFns || left[left.length - 1]?.FunctionIds || [];
      const blanks = Math.max(0, START_ROWS - left.length);
      return [...left, ...Array.from({ length: blanks }, () => newRow(fns))];
    });
    if (saved.size) refresh();
    const bad = errors.size;
    toast(`${saved.size} guest${saved.size === 1 ? '' : 's'} added${bad ? ` · ${bad} line${bad === 1 ? '' : 's'} need a fix` : ''}`);
  };

  return {
    quickOpen, openQuick, closeQuick, quickRows, quickFunctions, quickSaving, quickProgress, filledCount,
    setQuickField, toggleQuickFunction, toggleQuickColumn, columnState, addQuickRows, removeQuickRow, saveQuick,
  };
}
